class RCCarousel extends HTMLElement {

  constructor() {
    super();
    this._track = null;
    this._slides = [];
    this._index = 0;
    this._timer = null;
    this._gestureEnabled = true;
    this._autoplayMs = 0;
    this._drag = { active: false, startX: 0, currentX: 0, deltaX: 0 };
    this._onResize = this._onResize.bind(this);
    this._onKeydown = this._onKeydown.bind(this);
    this._onPointerDown = this._onPointerDown.bind(this);
    this._onPointerMove = this._onPointerMove.bind(this);
    this._onPointerUp = this._onPointerUp.bind(this);
    this._onClickCapture = this._onClickCapture.bind(this);
    this._suppressBannerLinkClick = false;
  };

  connectedCallback() {
    this._init();
  };

  disconnectedCallback() {
    this._cleanup();
  };

  _init() {
    this._track = this.querySelector('.rc-track');
    if(!this._track) return;
    this._slides = Array.from(this.querySelectorAll('.rc-slide'));
    if(this._slides.length === 0) return;
    const ds = this.dataset;
    this._gestureEnabled = this._truthy(ds.gesture, true);
    const speed = Number(ds.speed || 0);
    this._autoplayMs = this._truthy(ds.autoplay, false) && speed > 0 ? speed * 1000 : 0;
    this.style.touchAction = this._gestureEnabled ? 'pan-y' : 'auto';
    this._track.style.display = 'flex';
    this._track.style.willChange = 'transform';
    this._track.style.transition = 'transform 400ms ease';
    this._slides.forEach((el) => {
      el.style.flex = '0 0 100%';
      el.style.width = '100%';
    });
    this._bindNav();
    window.addEventListener('resize', this._onResize);
    this.addEventListener('keydown', this._onKeydown);
    this.addEventListener('click', this._onClickCapture, true);
    if(this._gestureEnabled) {
      this.addEventListener('pointerdown', this._onPointerDown, { passive: true });
      this.addEventListener('pointerup', this._onPointerUp);
      this.addEventListener('pointercancel', this._onPointerUp);
      this.addEventListener('pointermove', this._onPointerMove);
    };
    this._setupAutoplay();
    this.setAttribute('role', 'region');
    this.setAttribute('aria-roledescription', 'carousel');
    this.tabIndex = 0;
    this._goTo(0, false);
  };

  _bindNav() {
    const id = this.id;
    const innerDots = Array.from(this.querySelectorAll('.rc-dots [data-index]'));
    const outerDots = id ? Array.from(document.querySelectorAll(`.rc-dots[data-carousel="${id}"] [data-index]`)) : [];
    this._dotButtons = [...innerDots, ...outerDots];
    // Attempt initial cache of below-contents linked to this carousel
    this._belowContents = id ? Array.from(document.querySelectorAll(`.rc-below-contents[data-carousel="${id}"] .rc-below-content`)) : [];
    this._dotButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const idx = Number(btn.getAttribute('data-index')) || 0;
        this._goTo(idx);
      });
    });
    const innerArrows = Array.from(this.querySelectorAll('[data-dir]'));
    const outerArrows = id ? Array.from(document.querySelectorAll(`.rc-arrows[data-carousel="${id}"] [data-dir]`)) : [];
    this._arrowButtons = [...innerArrows, ...outerArrows];
    this._arrowButtons.forEach((btn) => {
      const dir = btn.getAttribute('data-dir');
      btn.addEventListener('click', () => (dir === 'prev' ? this.prev() : this.next()));
    });
  };

  _cleanup() {
    window.removeEventListener('resize', this._onResize);
    this.removeEventListener('keydown', this._onKeydown);
    this.removeEventListener('click', this._onClickCapture, true);
    this.removeEventListener('pointerdown', this._onPointerDown);
    this.removeEventListener('pointerup', this._onPointerUp);
    this.removeEventListener('pointercancel', this._onPointerUp);
    this.removeEventListener('pointermove', this._onPointerMove);
    this._clearAutoplay();
  };

  _truthy(val, fallback) {
    if(val == null) return fallback;
    const s = String(val).toLowerCase();
    return !(s === 'false' || s === '0' || s === 'no');
  };

  _onResize() {
    this._goTo(this._index, false);
  };

  _onKeydown(e) {
    if(e.key === 'ArrowRight') this.next();
    else if (e.key === 'ArrowLeft') this.prev();
  };

  _onClickCapture(e) {
    if(!this._suppressBannerLinkClick) return;
    const link = e.target && e.target.closest && e.target.closest('.rc-banner-link');
    if(link && this.contains(link)) {
      e.preventDefault();
      if(typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      else e.stopPropagation();
    }
    this._suppressBannerLinkClick = false;
  };

  _onPointerDown(e) {
    if(!this._gestureEnabled) return;
    this._suppressBannerLinkClick = false;
    this._drag.active = true;
    this._drag.startX = e.clientX;
    this._drag.currentX = e.clientX;
    this._drag.deltaX = 0;
    this._track.style.transition = 'none';
  };

  _onPointerMove(e) {
    if(!this._drag.active) return;
    this._drag.currentX = e.clientX;
    this._drag.deltaX = this._drag.currentX - this._drag.startX;
    const w = this.clientWidth;
    const offset = -this._index * w + this._drag.deltaX;
    this._track.style.transform = `translate3d(${offset}px, 0, 0)`;
  };

  _onPointerUp() {
    if(!this._drag.active) return;
    const w = this.clientWidth;
    const threshold = w * 0.15;
    this._track.style.transition = 'transform 400ms ease';
    if(this._drag.deltaX > threshold) this.prev();
    else if(this._drag.deltaX < -threshold) this.next();
    else this._goTo(this._index);
    this._suppressBannerLinkClick = this._gestureEnabled && Math.abs(this._drag.deltaX) > 10;
    this._drag.active = false;
    this._drag.startX = 0;
    this._drag.currentX = 0;
    this._drag.deltaX = 0;
  };

  _setupAutoplay() {
    this._clearAutoplay();
    if(!this._autoplayMs) return;
    // Respect users who prefer reduced motion: never auto-advance.
    if(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this._timer = setInterval(() => this.next(), this._autoplayMs);
    // Bind pause/resume listeners once. Pause on hover AND keyboard focus (WCAG 2.2.2).
    if(this._autoplayListenersBound) return;
    this._autoplayListenersBound = true;
    this.addEventListener('mouseenter', () => this._clearAutoplay());
    this.addEventListener('mouseleave', () => this._setupAutoplay());
    this.addEventListener('focusin', () => this._clearAutoplay());
    this.addEventListener('focusout', () => this._setupAutoplay());
  };

  _clearAutoplay() {
    if(this._timer) {
      clearInterval(this._timer);
      this._timer = null;
    };
  };

  _goTo(index, animate = true) {
    const max = this._slides.length - 1;
    this._index = Math.max(0, Math.min(index, max));
    this._track.style.transition = animate ? 'transform 400ms ease' : 'none';
    const offset = -this._index * this.clientWidth;
    this._track.style.transform = `translate3d(${offset}px, 0, 0)`;
    this._updateDots();
    this._updateBelowContents();
  };

  _updateDots() {
    if(!this._dotButtons) return;
    this._dotButtons.forEach((d) => {
      const i = Number(d.getAttribute('data-index')) || 0;
      if(i === this._index) d.setAttribute('aria-current', 'true');
      else d.removeAttribute('aria-current');
    });
  }

  _updateBelowContents() {
    if(!this.id) return;
    // Lazily (re)query if not yet available at init time
    if(!this._belowContents || this._belowContents.length === 0) {
      this._belowContents = Array.from(document.querySelectorAll(`.rc-below-contents[data-carousel="${this.id}"] .rc-below-content`));
    }
    if(!this._belowContents || this._belowContents.length === 0) return;
    this._belowContents.forEach((el, i) => {
      if(i === this._index) el.removeAttribute('hidden');
      else el.setAttribute('hidden', '');
    });
  }

  next() {
    const nextIndex = (this._index + 1) % this._slides.length;
    this._goTo(nextIndex);
  };

  prev() {
    const prevIndex = (this._index - 1 + this._slides.length) % this._slides.length;
    this._goTo(prevIndex);
  };

};

if(!customElements.get('rc-carousel')) {
  customElements.define('rc-carousel', RCCarousel);
};